import { useState, useEffect } from 'react';
import {
  Box, Typography, Stack, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Switch, FormControlLabel, CircularProgress,
  Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { CamposAdicionalesService } from '../services/camposAdicionalesService';
import type { CampoAdicional } from '../types/camposAdicionales';

const emptyCampo: CampoAdicional = { idCampo: 0, nombreCampo: '', tipoCampo: 'TEXTO', valorDefecto: null, maxCaracteres: 100, obligatorio: false, habilitado: true, orden: 0 };

export default function CamposAdicionalesPage() {
  const [campos, setCampos] = useState<CampoAdicional[]>([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState<CampoAdicional>(emptyCampo);
  const [loading, setLoading] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => { try { setCampos(await CamposAdicionalesService.getAll()); } catch { /* handled */ } };

  const handleSave = async () => {
    setLoading(true);
    try { await CamposAdicionalesService.save(form); setDialog(false); await load(); }
    catch { /* handled */ } finally { setLoading(false); }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Eliminar campo?')) return;
    try { await CamposAdicionalesService.delete(id); await load(); } catch { /* handled */ }
  };

  const columns: GridColDef[] = [
    { field: 'orden', headerName: '#', width: 60, type: 'number' },
    { field: 'nombreCampo', headerName: 'Nombre', flex: 1, minWidth: 200 },
    { field: 'tipoCampo', headerName: 'Tipo', width: 100 },
    { field: 'maxCaracteres', headerName: 'Max. Caracteres', width: 130, type: 'number' },
    { field: 'obligatorio', headerName: 'Obligatorio', width: 100, type: 'boolean' },
    { field: 'habilitado', headerName: 'Habilitado', width: 100, type: 'boolean' },
    { field: 'acciones', headerName: '', width: 120, sortable: false,
      renderCell: (p) => (
        <Stack direction="row" spacing={0.5}>
          <Button size="small" onClick={() => { setForm(p.row); setDialog(true); }}><EditIcon fontSize="small" /></Button>
          <Button size="small" color="error" onClick={() => handleDelete(p.row.idCampo)}><DeleteIcon fontSize="small" /></Button>
        </Stack>
      )},
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Campos Adicionales</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setForm(emptyCampo); setDialog(true); }}>Nuevo Campo</Button>
      </Stack>
      <DataGrid rows={campos} columns={columns} getRowId={(row) => row.idCampo} autoHeight density="compact" disableRowSelectionOnClick pageSizeOptions={[10, 25]} />

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{form.idCampo ? 'Editar Campo' : 'Nuevo Campo'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Nombre del Campo" fullWidth value={form.nombreCampo} onChange={(e) => setForm({ ...form, nombreCampo: e.target.value })} />
            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select value={form.tipoCampo} label="Tipo" onChange={(e) => setForm({ ...form, tipoCampo: e.target.value })}>
                <MenuItem value="TEXTO">Texto</MenuItem>
                <MenuItem value="NUMERO">Número</MenuItem>
                <MenuItem value="FECHA">Fecha</MenuItem>
                <MenuItem value="BOOLEAN">Sí/No</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Valor por Defecto" fullWidth value={form.valorDefecto ?? ''} onChange={(e) => setForm({ ...form, valorDefecto: e.target.value || null })} />
            <Stack direction="row" spacing={2}>
              <TextField label="Max. Caracteres" type="number" fullWidth value={form.maxCaracteres} onChange={(e) => setForm({ ...form, maxCaracteres: Number(e.target.value) })} />
              <TextField label="Orden" type="number" fullWidth value={form.orden} onChange={(e) => setForm({ ...form, orden: Number(e.target.value) })} />
            </Stack>
            <FormControlLabel control={<Switch checked={form.obligatorio} onChange={(e) => setForm({ ...form, obligatorio: e.target.checked })} />} label="Obligatorio" />
            <FormControlLabel control={<Switch checked={form.habilitado} onChange={(e) => setForm({ ...form, habilitado: e.target.checked })} />} label="Habilitado" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={loading}>{loading ? <CircularProgress size={18} /> : 'Guardar'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
